import type { FrameOutput, LatestFrameOutput } from '@meteoradar/shared/contract/frame.schema';
import type { HealthOutput } from '@meteoradar/shared/contract/health.schema';
import type { ProductOutput } from '@meteoradar/shared/contract/product.schema';
import type { ProductCode } from '@meteoradar/shared/enums/product-code';

export class ApiError extends Error {
  public constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export type FetchLike = typeof fetch;

export class ApiClient {
  public constructor(private readonly fetchFn: FetchLike = (...args) => fetch(...args), private readonly baseUrl = '') {}

  public async getProducts(): Promise<ProductOutput[]> {
    return await this.get<ProductOutput[]>('/api/products');
  }

  public async getFrames(product: ProductCode, hours: number): Promise<FrameOutput[]> {
    return await this.get<FrameOutput[]>(`/api/frames?product=${product}&hours=${hours}`);
  }

  public async getLatest(product: ProductCode): Promise<LatestFrameOutput | null> {
    try {
      return await this.get<LatestFrameOutput>(`/api/frames/latest?product=${product}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === HTTP_NOT_FOUND) {
        return null;
      }
      throw error;
    }
  }

  public async getHealth(): Promise<HealthOutput> {
    return await this.get<HealthOutput>('/api/health');
  }

  private async get<T>(path: string): Promise<T> {
    const response = await this.fetchFn(`${this.baseUrl}${path}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) {
      let message = response.statusText;
      try {
        const body = (await response.json()) as { message?: string };
        message = body.message ?? message;
      } catch {
        message = response.statusText;
      }
      throw new ApiError(response.status, message);
    }
    return (await response.json()) as T;
  }
}

const HTTP_NOT_FOUND = 404;
